import React, { useCallback, useMemo, useState } from 'react';
import {
  type PurchaseInvoiceReportDocumentEntryState,
  resolvePurchaseInvoiceReportDocumentEntry
} from './utils/document-entry';
import PurchaseInvoiceReportDocumentEntry from './components/DocumentEntry';
import { ErpDataTableV2, type TableData } from '@/components/custom/erp';
import { EditPrintTemplateDialog } from '@/components/custom/erp';
import { useDynamicSearch, useReport, useRows } from '@/hooks';
import { invoiceReportColumns } from './cols-definition';
import { BangKeHoaDonMuaHangProvider } from './context';
import SearchDialog from './components/SearchDialog';
import { ActionBar } from './components';
import { QUERY_KEYS } from '@/constants';
import { useDialogState } from './hooks';

function BangKeHoaDonMuaHangPageContent() {
  const { getReport, isLoading, data, currentPage, handlePageChange, totalItems } = useReport({
    endpoint: QUERY_KEYS.BANG_KE_HOA_DON_MUA_HANG,
    pageSize: 50
  });

  const {
    initialSearchDialogOpen,
    editPrintTemplateDialogOpen,
    showTable,
    searchParams,
    handleInitialSearch: handleInitialSearchHook,
    handleInitialSearchClose,
    handleSearchClick,
    handleEditPrintTemplateClick,
    handleClosePrintTemplateDialog,
    handleSavePrintTemplate,
    handleFixedColumnsClick,
    handleExportDataClick
  } = useDialogState();
  const { handleRowClick } = useRows();
  const [documentEntry, setDocumentEntry] = useState<PurchaseInvoiceReportDocumentEntryState | null>(null);
  const handleDocumentClick = useCallback((row: MuaHangRecord) => {
    setDocumentEntry(resolvePurchaseInvoiceReportDocumentEntry(row));
  }, []);
  const columns = useMemo(() => invoiceReportColumns(handleDocumentClick), [handleDocumentClick]);
  const {
    searchState,
    handleSearchChange,
    handleSearchPageChange,
    hasFilterParams,
    handleInitialSearch,
    handleRefreshClick,
    isSearching
  } = useDynamicSearch({
    onSearch: getReport,
    params: searchParams,
    onInitialSearch: handleInitialSearchHook,
    getColumns: () => columns,
    endpoint: QUERY_KEYS.BANG_KE_HOA_DON_MUA_HANG,
    pageSize: 50
  });

  const handleCloseDocumentEntry = useCallback(() => {
    setDocumentEntry(null);
  }, []);

  const handleDocumentMutated = useCallback(async () => {
    if (hasFilterParams) {
      await handleRefreshClick();
      return;
    }

    if (searchParams) {
      await getReport(searchParams);
    }
  }, [getReport, handleRefreshClick, hasFilterParams, searchParams]);

  const resolvedRows = hasFilterParams ? searchState.searchResults : data;
  const isTableLoading = isLoading || isSearching;
  const resolvedCurrentPage = hasFilterParams ? searchState.currentPage : currentPage;
  const resolvedTotalItems = hasFilterParams ? searchState.totalCount : totalItems;
  const resolvedHandlePageChange = hasFilterParams ? handleSearchPageChange : handlePageChange;

  const tables: TableData[] = [
    {
      name: '',
      rows: isTableLoading ? [] : resolvedRows || [],
      columns
    }
  ];

  return (
    <div className='flex h-screen w-screen flex-col lg:overflow-hidden'>
      {initialSearchDialogOpen && (
        <SearchDialog onClose={handleInitialSearchClose} onSearch={handleInitialSearch} initialValues={searchParams} />
      )}

      {documentEntry && (
        <div className='relative h-full min-h-0 w-full'>
          <PurchaseInvoiceReportDocumentEntry
            document={documentEntry}
            onClose={handleCloseDocumentEntry}
            onMutated={handleDocumentMutated}
          />
        </div>
      )}

      <EditPrintTemplateDialog
        open={editPrintTemplateDialogOpen}
        onClose={handleClosePrintTemplateDialog}
        onSave={handleSavePrintTemplate}
      />

      {showTable && !documentEntry && (
        <div className='flex h-full min-h-0 w-full flex-col'>
          <ActionBar
            onSearchClick={handleSearchClick}
            onPrintClick={() => {}}
            onRefreshClick={handleRefreshClick}
            onFixedColumnsClick={handleFixedColumnsClick}
            onExportDataClick={handleExportDataClick}
            onEditPrintTemplateClick={handleEditPrintTemplateClick}
            fromDate={searchParams.ngay_ct1 || ''}
            toDate={searchParams.ngay_ct2 || ''}
            isSearching={isSearching}
          />

          <div className='relative min-h-0 flex-1 overflow-hidden'>
            <ErpDataTableV2
              tables={tables}
              onRowClick={handleRowClick}
              totalItems={resolvedTotalItems}
              pageSize={50}
              currentPage={resolvedCurrentPage}
              onPageChange={resolvedHandlePageChange}
              onSearchChange={handleSearchChange}
              loading={isTableLoading}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default function BangKeHoaDonMuaHangPage() {
  return (
    <BangKeHoaDonMuaHangProvider>
      <BangKeHoaDonMuaHangPageContent />
    </BangKeHoaDonMuaHangProvider>
  );
}
